import { isEmpty } from "lodash";
import { forwardRef, useCallback, useImperativeHandle, useRef, type KeyboardEvent } from "react";
import { Input, type InputOnChangeData } from "semantic-ui-react";
import { ClearInputButton } from "../../buttons/clearInputButton/ClearInputButton";
import styles from "./InputField.module.scss";
import ValidatedField, { type ValidatedFieldProps } from "../ValidatedField";
import { type FormikHandlers } from "formik";

export interface InputFieldProps extends ValidatedFieldProps {
  inputType?: string;
  onBlur?: (propertyName: string) => void;
  disabled?: boolean;
  autoFocus?: boolean;
  maxLength?: number;
  className?: string;
  setFieldValue?: (propertyName: string, value: any, shouldValidate?: boolean) => void;
  testId?: string;
  action?: Object;
  loading?: boolean;
  iconPosition?: "left";
  autoComplete?: string;
  icon?: string | React.ReactNode;
  onKeyPress?: (event: KeyboardEvent<HTMLInputElement>) => void;
  onKeyDown?: (event: KeyboardEvent<HTMLInputElement>) => void;
  nullable?: boolean;
  mask?: RegExp;
  clearable?: boolean;
  onValueChanged?: (data: { value: any }) => void;
  adornmentAfter?: React.ReactNode;
  gap?: number;
  isReadOnlyView?: boolean;
  readOnlyEmptyState?: string;
}

const InputField = forwardRef<HTMLInputElement, Omit<Partial<FormikHandlers>, "handleChange"> & InputFieldProps>(
  (props, ref) => {
    const {
      value,
      propertyName,
      setFieldValue,
      handleChange,
      handleBlur,
      onBlur,
      disabled,
      autoFocus,
      maxLength,
      nullable,
      clearable,
      placeholder = "",
      className,
      mask,
      testId,
      onValueChanged,
      inputType = "text",
      action,
      loading,
      iconPosition,
      autoComplete = undefined,
      icon,
      onKeyPress,
      onKeyDown,
      label,
      adornmentAfter,
      isReadOnlyView,
      readOnlyEmptyState,
    } = props;

    const internalRef = useRef<any>(null);

    useImperativeHandle(ref, () => internalRef?.current?.inputRef?.current as HTMLInputElement, []);

    const handleFieldBlur = useCallback(
      (e: React.FocusEvent<HTMLInputElement>) => {
        handleBlur && handleBlur(e);
        onBlur && onBlur(propertyName);
      },
      [handleBlur, onBlur, propertyName],
    );
    const handleFieldChange = useCallback(
      (event: React.ChangeEvent<HTMLInputElement>, data: InputOnChangeData) => {
        const maskedValue = (v: string) => (v?.match(mask || "") ? v : null);
        const fieldValue = mask ? maskedValue(data.value) : data.value;

        if (fieldValue == null && !!mask) return;

        if (nullable && fieldValue === "") {
          setFieldValue?.(propertyName, null, true);
        } else {
          setFieldValue?.(propertyName, fieldValue, true);
        }

        handleChange && handleChange(event, data);
        onValueChanged && onValueChanged(data);
      },
      [handleChange, nullable, propertyName, setFieldValue, mask, onValueChanged],
    );

    const renderClearButton = useCallback(() => {
      const clear = () => setFieldValue?.(propertyName, "");

      if (clearable && !isEmpty(value)) {
        return <ClearInputButton onClick={clear} disabled={disabled} />;
      }
    }, [clearable, disabled, propertyName, setFieldValue, value]);

    if (isReadOnlyView) {
      const useEmptyState = value === "";

      return (
        <div className={styles.readOnlyWrapper}>
          <span>{label}</span>
          <div className={styles.readOnlyValue}>{useEmptyState ? readOnlyEmptyState : (value as string)}</div>
        </div>
      );
    }

    return (
      <ValidatedField {...props}>
        <div className={styles.container}>
          <div className={styles.field}>
            <Input
              ref={internalRef}
              fluid
              data-testid={testId || "input-field"}
              name={propertyName}
              type={inputType}
              value={value !== null && value !== undefined ? value : ""}
              placeholder={placeholder}
              onChange={handleFieldChange}
              onBlur={handleFieldBlur}
              disabled={disabled}
              autoFocus={autoFocus}
              maxLength={maxLength}
              icon={icon || renderClearButton()}
              className={className}
              action={action}
              loading={loading}
              iconPosition={iconPosition}
              autoComplete={autoComplete}
              onKeyPress={onKeyPress}
              onKeyDown={onKeyDown}
              aria-label={label}
              onWheel={(e: React.WheelEvent<HTMLInputElement>) => e.currentTarget.blur()}
            />
          </div>
          {adornmentAfter}
        </div>
      </ValidatedField>
    );
  },
);

export default InputField;
